import { useGetBrands, usePostBrand } from '@/api/brands'
import type { Brand } from '@/api/brands/type'
import { useGetVideoDetail, usePutVideoSetting } from '@/api/videos'
import Header from '@/components/common/Header'
import ManageInfoItem from '@/components/manage/ManageInfoItem'
import ManageVideoDetailBrandInfo from '@/components/manage/videos/videoDetail/BrandInfo'
import VideoDetailHeader from '@/components/manage/videos/videoDetail/Header'
import ManageVideoDetailSearchResult from '@/components/manage/videos/videoDetail/SerachResult'
import ManageVideoItem from '@/components/manage/videos/VideoItem'
import { AdTypeOptions } from '@/constants/selectOptions'
import type { ADType, BrandId } from '@/types/common'
import { isRegistered } from '@/utils/video'
import styled from '@emotion/styled'
import { Button, Flex, SearchInput, Select, useToastPopup } from '@collable/ui'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

interface AdInfo {
  adType: Exclude<ADType, null> | ''
  brand: Brand | null
}

const EMPTY_AD_INFO: AdInfo = {
  adType: '',
  brand: null,
}

const toAdInfo = (
  videoDetail?: {
    adType?: ADType | null
    brandId?: BrandId | null
    brandName?: string | null
    brandLogoUrl?: string | null
  } | null,
): AdInfo => {
  if (!videoDetail) {
    return EMPTY_AD_INFO
  }

  return {
    adType: videoDetail.adType || '',
    brand: videoDetail.brandId
      ? {
          brandId: videoDetail.brandId,
          brandName: videoDetail.brandName ?? '',
          logoUrl: videoDetail.brandLogoUrl ?? '',
          status: 'ACTIVATED',
          categoryCode: '',
        }
      : null,
  }
}

const isSameAdInfo = (left: AdInfo, right: AdInfo) => {
  return (
    left.adType === right.adType &&
    left.brand?.brandId === right.brand?.brandId &&
    left.brand?.brandName === right.brand?.brandName &&
    left.brand?.logoUrl === right.brand?.logoUrl
  )
}

const VideoDetailPage = () => {
  const { videoId } = useParams()
  const navigate = useNavigate()
  const { addToastPopup } = useToastPopup()

  const id = Number(videoId)

  const { data: videoDetail } = useGetVideoDetail(id)
  const [keyword, setKeyword] = useState('')
  const [adInfo, setAdInfo] = useState<AdInfo>(EMPTY_AD_INFO)
  const { mutate: putVideoSetting } = usePutVideoSetting(id)
  const { mutate: createBrand } = usePostBrand()

  const { data: brands } = useGetBrands(keyword)

  const isEdit = !!videoDetail && isRegistered(videoDetail)

  const originalAdInfo = useMemo(() => toAdInfo(videoDetail), [videoDetail])

  const disabled = useMemo(() => {
    const isChanged = !isSameAdInfo(originalAdInfo, adInfo)
    if (!isChanged) return true

    if (!adInfo.adType && adInfo.brand) return true

    return !!adInfo.adType && !adInfo.brand
  }, [adInfo, originalAdInfo])

  const onClickSave = () => {
    if (disabled) return
    // 신규 생성의 경우
    if (adInfo.brand && !adInfo.brand.brandId) {
      createBrand(
        {
          brandNameKr: adInfo.brand.brandName,
          brandNameEn: '',
          categoryCode: '',
          managerEmail: '',
        },
        {
          onSuccess: res => {
            saveVideoSetting({
              brandId: res.brandId,
              brandName: res.brandName,
              adType: adInfo.adType || null,
            })
          },
        },
      )
      return
    }
    saveVideoSetting({
      brandId: adInfo.brand?.brandId ?? null,
      brandName: adInfo.brand?.brandName ?? null,
      adType: adInfo.adType || null,
    })
  }

  const saveVideoSetting = ({
    brandId,
    brandName,
    adType,
  }: {
    brandId: BrandId | null
    brandName: string | null
    adType: ADType | null
  }) => {
    putVideoSetting(
      {
        brandId,
        brandName,
        adType,
        productName: '',
        highlight: 0,
      },
      {
        onSuccess: () => {
          navigate(-1)
          addToastPopup({
            type: 'normal',
            message: isEdit ? '광고 정보를 수정했어요' : '광고 정보를 등록했어요',
            status: 'positive',
          })
        },
      },
    )
  }

  useEffect(() => {
    if (videoDetail) {
      setAdInfo(originalAdInfo)
    }
  }, [originalAdInfo, videoDetail])

  return (
    <div>
      <Header selectedMenu="videos" />
      <Contents as="main" direction="column" gap={48}>
        <VideoDetailHeader isEdit={isEdit} />
        {videoDetail && <ManageVideoItem video={videoDetail} contentsOnly />}
        <Flex direction="column" gap={12}>
          <ManageInfoItem title="1. 유료 광고 타입">
            <Select
              options={AdTypeOptions}
              placeholder="유료광고 미포함"
              size="large"
              type="outline"
              width={320}
              selectedOption={adInfo.adType}
              onChange={option => {
                setAdInfo(prev => ({
                  ...prev,
                  adType: option,
                }))
              }}
            />
          </ManageInfoItem>
          <ManageInfoItem
            title="2. 유료 광고 브랜드"
            required
            resultComponent={
              brands && !adInfo.brand ? (
                <ManageVideoDetailSearchResult
                  keyword={keyword}
                  brands={brands}
                  onSelectBrand={brand => {
                    setAdInfo(prev => ({
                      ...prev,
                      brand,
                    }))
                  }}
                />
              ) : null
            }
          >
            {adInfo.brand ? (
              <ManageVideoDetailBrandInfo
                brand={adInfo.brand}
                onDelete={() => {
                  setAdInfo(prev => ({
                    ...prev,
                    brand: null,
                  }))
                }}
              />
            ) : (
              <SearchInput
                size="large"
                width={320}
                placeholder="광고 브랜드 검색"
                initialValue={keyword}
                onEnter={e => {
                  setKeyword(e.currentTarget.value)
                }}
              />
            )}
          </ManageInfoItem>
        </Flex>
        <Flex gap={12} justifyContent="flex-end">
          <Button size="large" color="neutral" kind="soft" onClick={() => navigate(-1)}>
            취소
          </Button>
          <Button
            size="large"
            color="primary"
            kind="solid"
            disabled={disabled}
            onClick={onClickSave}
          >
            등록하기
          </Button>
        </Flex>
      </Contents>
    </div>
  )
}

export default VideoDetailPage

const Contents = styled(Flex)`
  padding: 48px 0;
  max-width: 1320px;
  margin: 0 auto;
`
